import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@/lib/utils"

const TooltipProvider = TooltipPrimitive.Provider

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  /** Phase 1.12A foundation — when set, wraps Content in TooltipPrimitive.Portal
   * with this container. Required for Tooltip rendered inside iframe contexts
   * (Puck canvas) so Floating UI's transform math resolves correctly against
   * the iframe viewport. See @/lib/data/display/iframe-safe-portal. */
  portalContainer?: HTMLElement | null;
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(({ className, sideOffset = 4, portalContainer, ...props }, ref) => {
  const content = (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  );
  // When no portalContainer passed → render inline (Radix default behavior preserved).
  // When portalContainer set → wrap in Portal so popper mounts to the specified
  // element (typically the iframe's document.body via useIframeSafePortalContainer).
  if (portalContainer === undefined) return content;
  return (
    <TooltipPrimitive.Portal container={portalContainer ?? undefined}>
      {content}
    </TooltipPrimitive.Portal>
  );
})
TooltipContent.displayName = TooltipPrimitive.Content.displayName

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
